import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Footprints, Clock, CheckCircle, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function CardioGuide() {
  const navigate = useNavigate();

  const cardioOptions = [
    { activity: "Brisk Walking", intensity: "Moderate", calories: "200-300/30min", joints: "Low impact", tip: "Swing your arms, keep pace where you can talk but are slightly breathless" },
    { activity: "Cycling", intensity: "Moderate-High", calories: "250-400/30min", joints: "Very low impact", tip: "Great for bad knees; outdoor or stationary both work" },
    { activity: "Swimming", intensity: "Moderate-High", calories: "300-400/30min", joints: "Zero impact", tip: "Perfect if you have joint issues; works entire body" },
    { activity: "Dance Workout", intensity: "High", calories: "300-450/30min", joints: "Moderate impact", tip: "Fun and doesn't feel like exercise; follow YouTube videos" },
    { activity: "Elliptical", intensity: "Moderate", calories: "250-350/30min", joints: "Low impact", tip: "Mimics running without the joint stress" },
    { activity: "Rowing", intensity: "High", calories: "300-400/30min", joints: "Low impact", tip: "Full body workout; builds strength and cardio together" }
  ];

  const heartRateZones = [
    { zone: "Zone 1 (50-60%)", feeling: "Very light, can sing", purpose: "Warm-up, recovery" },
    { zone: "Zone 2 (60-70%)", feeling: "Light, can talk easily", purpose: "Fat burning, endurance base" },
    { zone: "Zone 3 (70-80%)", feeling: "Moderate, can talk in short sentences", purpose: "Aerobic fitness, stamina" },
    { zone: "Zone 4 (80-90%)", feeling: "Hard, can only say a few words", purpose: "Performance improvement" },
    { zone: "Zone 5 (90-100%)", feeling: "Maximum effort", purpose: "Short bursts only" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-4">
            Week 4 • Day 4 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Heart-Pumping Cardio
          </h1>
          <p className="text-xl text-secondary-600">Strengthen Your Heart, Boost Your Mood</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Heart className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Goal: 30 Minutes</h3>
              <p className="text-red-100 text-lg">
                Choose your favorite cardio activity and move for 30 minutes. 
                Keep intensity at "can talk but slightly breathless." Track your distance or steps.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Cardio Matters</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "Heart Health", desc: "Reduces risk of heart disease by 50%" },
              { title: "Brain Power", desc: "Improves memory and reduces dementia risk" },
              { title: "Mood Boost", desc: "Releases endorphins that fight depression" },
              { title: "Better Sleep", desc: "Improves sleep quality and duration" },
              { title: "Energy Levels", desc: "Increases stamina for daily activities" },
              { title: "Longevity", desc: "Regular cardio can add years to your life" }
            ].map((item, i) => (
              <div key={i} className="p-4 bg-red-50 rounded-xl text-center">
                <h3 className="font-bold text-red-700 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Cardio Options */}
        <section className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 border-2 border-red-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Footprints className="w-6 h-6 text-red-500" />
            Choose Your Cardio
          </h2>
          <div className="space-y-4">
            {cardioOptions.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-2">
                  <h3 className="font-bold text-red-700 md:w-1/4">{item.activity}</h3>
                  <span className="text-sm bg-red-100 text-red-600 px-2 py-1 rounded-full w-fit">{item.intensity}</span>
                  <span className="text-sm text-secondary-600 flex items-center gap-1">
                    <Flame className="w-4 h-4 text-orange-500" />
                    {item.calories}
                  </span>
                  <span className="text-sm text-green-600">{item.joints}</span>
                </div>
                <p className="text-sm text-secondary-600 italic">{item.tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Heart Rate Zones */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6 text-red-500" />
            Heart Rate Zones
          </h2>
          <p className="text-secondary-600 mb-4">Max heart rate = 220 - your age. For most workouts, aim for Zone 2-3.</p>
          <div className="space-y-3">
            {heartRateZones.map((zone, index) => (
              <div key={index} className="flex items-center gap-4 p-3 bg-red-50 rounded-lg">
                <div className={`w-4 h-4 rounded-full ${
                  index === 0 ? 'bg-green-400' : index === 1 ? 'bg-green-500' : 
                  index === 2 ? 'bg-yellow-500' : index === 3 ? 'bg-orange-500' : 'bg-red-500'
                }`}></div>
                <div className="flex-1">
                  <span className="font-bold text-secondary-900">{zone.zone}</span>
                  <p className="text-sm text-secondary-600">{zone.feeling}</p>
                </div>
                <span className="text-sm text-red-600 bg-white px-2 py-1 rounded">{zone.purpose}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default CardioGuide;
